import { SkeletonHoldScope } from "@baton/ui";
import { lazy, Suspense, type ReactNode } from "react";
import { createBrowserRouter, type RouteObject } from "react-router";
import { NotFound } from "./errors/NotFound.tsx";
import { RouteError } from "./errors/RouteError.tsx";
import { AccountsFallback, MeetingsFallback, ProjectItemsFallback } from "./routes/fallbacks.tsx";
import { NavigationFocusMark } from "./routes/meta.ts";
import { OutsideShell } from "./routes/OutsideShell.tsx";
import { ClientInsightsRedirect, CycleActiveRedirect, ProjectRedirect, ProjectSettingsRedirect, RootRedirect, WorkspaceIndexRedirect, WorkspaceSettingsRedirect, LegacyRedirect } from "./routes/redirects.tsx";
import { AccountHubSkeleton, AccountsPageSkeleton, ActivityPageSkeleton, AllItemsSkeleton, HomeSkeleton, CycleDetailSkeleton, CyclesListSkeleton, EmailPageSkeleton, MeetingPageSkeleton, ModuleDetailSkeleton, InboxSkeleton, ItemDetailSkeleton, LibrarySkeleton, MyWorkSkeleton, OverviewSkeleton, ProjectsIndexSkeleton, SettingsFrameSkeleton, SettingsPageSkeleton, SettingsRailSkeleton, UnassignedEmailsSkeleton, ViewsManagementSkeleton, WizardSkeleton } from "./skeletons/Skeletons.tsx";
import { trackOrigins } from "./shell/history/origin.ts";
import { WorkspaceEntry } from "./shell/layout/WorkspaceEntry.tsx";

/**
 * The route table of `contracts/screens.md` § Route map (`research.md` W-03). `paths` is the
 * typed set every link in the client is checked against; the router below mounts only the
 * routes whose screen exists, because an undelivered surface is absent from the router
 * rather than present and disabled (constitution § IV, FR-015-002, FR-015-071). The public
 * routes are the public entry's (`public/routes.tsx`). Each later slice adds its screens
 * here as it delivers them. Every screen beyond the shell (the collection machinery, the item
 * detail, Home, Overview, the inbox, the project screens, the cycles, the modules, the clients and client groups, the project, workspace and profile settings, instance administration, onboarding, desktop authorisation) is a dynamic import at its route boundary (`research.md` W-26, `plan.md`
 * § Package layout: each layout code split), so the shell stays inside the 250 KB budget.
 */
export const paths = {
  root: "/",
  signIn: "/sign-in",
  signUp: "/sign-up",
  magicCode: "/magic-code",
  resetPassword: "/reset-password",
  resetPasswordConfirm: "/reset-password/confirm",
  invite: "/invite/:token",
  authCallback: "/auth/callback",
  linkIdentity: "/auth/link-identity",
  desktopAuthorize: "/desktop/authorize",
  verifyEmail: "/verify-email",
  onboarding: "/onboarding",
  welcome: "/welcome",
  newWorkspace: "/new-workspace",
  workspace: "/:ws",
  dashboard: "/:ws/dashboard",
  overview: "/:ws/overview",
  inbox: "/:ws/inbox",
  myWork: "/:ws/my-work",
  allItems: "/:ws/all-tasks",
  search: "/:ws/search",
  activity: "/:ws/activity",
  projects: "/:ws/projects",
  project: "/:ws/projects/:project",
  projectItems: "/:ws/projects/:project/tasks",
  projectViews: "/:ws/projects/:project/views",
  projectView: "/:ws/projects/:project/views/:view",
  accounts: "/:ws/clients",
  account: "/:ws/clients/:account",
  accountTasks: "/:ws/clients/:account/tasks",
  /** A client's meetings, linked, attended by its contacts or linking it (018 FR-018-360, 2026-10-21), which Calls and meetings' All meetings opens. */
  accountMeetings: "/:ws/clients/:account/meetings",
  accountActivity: "/:ws/clients/:account/activity",
  accountInsights: "/:ws/clients/:account/insights",
  accountMeeting: "/:ws/clients/:account/meetings/:meeting",
  /** A contact of a client in the side peek over the client's page (020 FR-020-290, 2026-10-15): the page stays under it, so closing the peek is the client's page again. */
  accountContact: "/:ws/clients/:account/contacts/:contact",
  /** A task opened under a client (2026-10-07, 018 FR-018-238's pattern): the task's page with the rail kept on Clients. */
  accountItem: "/:ws/clients/:account/tasks/:item",
  /** A client's email as its full page (022 FR-022-364 as amended 2026-10-07), under the client so the rail stays on Clients. */
  accountEmail: "/:ws/clients/:account/emails/:email",
  accountGroups: "/:ws/client-groups",
  /** 028 FR-028-045 (2026-10-21): the Tickets mode's landing, every client's tickets for a portal worker and a client member's own. */
  tickets: "/:ws/tickets",
  /** One client's tickets, by the client as its page is (028 FR-028-045). */
  ticketsAccount: "/:ws/tickets/:account",
  /** One ticket, under its client's tickets (028 FR-028-045, slice 3). */
  ticket: "/:ws/tickets/:account/:request",
  /** A client's conversations and one standalone thread, under its tickets (028 FR-028-045, slice 5). */
  ticketThreads: "/:ws/tickets/:account/threads",
  ticketThread: "/:ws/tickets/:account/threads/:thread",
  /** 020 FR-020-281 (2026-10-03): every open task that reports a client, across clients. */
  clientItems: "/:ws/client-tasks",
  /** 020 FR-020-280 (2026-10-03): the one-page status of every client, printed or saved as CSV. */
  clientStatusReport: "/:ws/clients/status-report",
  unassignedEmails: "/:ws/unassigned-emails",
  /** An Unassigned email as its full page (022 FR-022-364 as amended 2026-10-07). */
  unassignedEmail: "/:ws/unassigned-emails/:email",
  library: "/:ws/library",
  meetings: "/:ws/meetings",
  meetingsDecide: "/:ws/meetings/to-decide",
  meeting: "/:ws/meetings/:meeting",
  cycles: "/:ws/sprints",
  cycleActive: "/:ws/sprints/active",
  backlog: "/:ws/backlog",
  archive: "/:ws/archive",
  cycle: "/:ws/sprints/:cycle",
  modules: "/:ws/projects/:project/modules",
  module: "/:ws/projects/:project/modules/:module",
  projectMeetings: "/:ws/projects/:project/meetings",
  projectInsights: "/:ws/projects/:project/insights",
  projectSettings: "/:ws/projects/:project/settings/:page",
  item: "/:ws/tasks/:item",
  workspaceViews: "/:ws/views",
  workspaceView: "/:ws/views/:view",
  workspaceSettings: "/:ws/settings/:page",
  workspaceSettingsRecord: "/:ws/settings/:page/:detail",
  /** 022 FR-022-213: a plan's page keeps `planner` between the Microsoft 365 page and the link's id. */
  workspaceSettingsPrefixedRecord: "/:ws/settings/:page/:sub/:detail",
  profileSettings: "/settings/:page",
  instance: "/instance/:page",
} as const;

export type RoutePath = (typeof paths)[keyof typeof paths];

/** The routes delivered so far, which is what the route-graph test walks (SC-015-011). */
export const deliveredRoutes: readonly RoutePath[] = [paths.root, paths.onboarding, paths.welcome, paths.newWorkspace, paths.workspace, paths.dashboard, paths.overview, paths.inbox, paths.myWork, paths.allItems, paths.projects, paths.project, paths.projectItems, paths.accounts, paths.account, paths.accountTasks, paths.accountMeetings, paths.accountActivity, paths.accountInsights, paths.accountMeeting, paths.accountContact, paths.accountItem, paths.accountEmail, paths.accountGroups, paths.tickets, paths.ticketsAccount, paths.ticket, paths.ticketThreads, paths.ticketThread, paths.clientItems, paths.clientStatusReport, paths.unassignedEmails, paths.unassignedEmail, paths.library, paths.meetings, paths.meetingsDecide, paths.meeting, paths.cycles, paths.cycleActive, paths.backlog, paths.archive, paths.cycle, paths.modules, paths.module, paths.projectMeetings, paths.projectInsights, paths.projectSettings, paths.item, paths.workspaceSettings, paths.workspaceSettingsRecord, paths.workspaceSettingsPrefixedRecord, paths.profileSettings, paths.instance, paths.projectViews, paths.projectView, paths.workspaceViews, paths.workspaceView, paths.search, paths.activity, paths.desktopAuthorize];

/** The lazy chunks, each loaded at its route boundary and prefetched on idle by the shell. */
export const chunks = {
  collection: () => import("./collection/list/Collection.tsx"),
  allItems: () => import("./all-items/AllItems.tsx"),
  dashboard: () => import("./dashboard/Home.tsx"),
  overview: () => import("./dashboard/Overview.tsx"),
  myWork: () => import("./my-work/MyWork.tsx"),
  item: () => import("./item/detail/ItemDetail.tsx"),
  inbox: () => import("./inbox/Inbox.tsx"),
  projects: () => import("./project/ProjectsIndex.tsx"),
  onboarding: () => import("./onboarding/Onboarding.tsx"),
  welcome: () => import("./onboarding/Welcome.tsx"),
  newWorkspace: () => import("./onboarding/NewWorkspace.tsx"),
  newProject: () => import("./project/NewProjectDialog.tsx"),
  accounts: () => import("./accounts/list/AccountList.tsx"),
  accountPage: () => import("./accounts/page/AccountPage.tsx"),
  accountTasks: () => import("./accounts/page/AccountTasks.tsx"),
  accountMeetings: () => import("./accounts/page/AccountMeetings.tsx"),
  accountActivity: () => import("./accounts/page/AccountActivity.tsx"),
  accountInsights: () => import("./meetings/client/ClientInsightsPage.tsx"),
  accountGroups: () => import("./accounts/groups/GroupList.tsx"),
  /** 022's Unassigned emails (2026-09-28): the Clients area's list of the intake emails Baton could not put on one client. */
  unassignedEmails: () => import("./m365/unassigned/UnassignedEmails.tsx"),
  /** 022's email as its full page (2026-10-07): the reader's third frame, a client's email or an Unassigned one. */
  emailPage: () => import("./m365/emails/EmailPage.tsx"),
  /** 020's Client tasks (2026-10-03, named Client items until 2026-10-13): the open client tasks across clients, by milestone. */
  clientItems: () => import("./accounts/clientItems/ClientItems.tsx"),
  /** 020's status report (2026-10-03): every client's standing on one page. */
  clientStatusReport: () => import("./accounts/statusReport/StatusReport.tsx"),
  /** 028's portal shell (2026-10-21): the frame a client member works in, loaded by them alone. */
  portalShell: () => import("./portal/shell/PortalShell.tsx"),
  /** 028's Tickets landing and one client's tickets page (2026-10-21). */
  tickets: () => import("./portal/home/TicketsHome.tsx"),
  portalPage: () => import("./portal/page/PortalPage.tsx"),
  /** 028's ticket page (slice 3). */
  ticketPage: () => import("./portal/requests/TicketPage.tsx"),
  /** 028's Messages list and thread page (slice 5). */
  messagesPage: () => import("./portal/threads/MessagesPage.tsx"),
  /** 022's Library (2026-09-29): the Team's shared SharePoint folders as tabs of documents. */
  library: () => import("./m365/library/Library.tsx"),
  meetings: () => import("./meetings/list/MeetingList.tsx"),
  meeting: () => import("./meetings/page/MeetingPage.tsx"),
  /** The Team's queue of decisions, To decide (018 FR-018-348, 2026-10-03). */
  meetingsDecide: () => import("./meetings/decide/DecideQueue.tsx"),
  /** The Clients dialogs the shell opens from anywhere: New client and Log a call or meeting (2026-09-27). */
  accountDialogs: () => import("./accounts/dialogs.tsx"),
  /** Move to project (2026-09-29): the dialog every move of a task goes through, opened from anywhere. */
  moveDialog: () => import("./item/move/MoveDialog.tsx"),
  /** The frame of a task open in the side peek or the item dialog (FR-015-040): prefetched on idle by the shell, so opening a task waits on the detail alone. */
  peekFrame: () => import("./shell/peek/ItemPeekFrame.tsx"),
  /** Why is it cancelled? (001 FR-161, 2026-10-03): the question every cancel of a task asks, opened from anywhere. */
  closeReasonDialog: () => import("./item/closure/CloseReasonDialog.tsx"),
  /** Remind (020 FR-020-278, 2026-10-03): the dialog a task's row menu and header open. */
  remindDialog: () => import("./item/remind/RemindDialog.tsx"),
  /** The record reference every rendered body draws its record links with (FR-015-150 § URL schemes as amended 2026-10-15): prefetched on idle by the shell, which draws a link without its address until it lands. */
  recordReference: () => import("./platform/references/RecordReference.tsx"),
  /** The keyboard shortcut reference (2026-10-13): prefetched on idle by the shell and mounted on its first opening. */
  shortcutReference: () => import("./shell/keyboard/ShellShortcutReference.tsx"),
  /** 019's assistant panel (2026-10-14): prefetched on idle by the shell and mounted while the panel is open. */
  assistant: () => import("./assistant/AssistantDock.tsx"),
  cycles: () => import("./cycle/list/CyclesList.tsx"),
  cycle: () => import("./cycle/detail/CycleDetail.tsx"),
  /** The Team's backlog (2026-10-02): its open tasks no sprint holds, a page of its own beside the sprints. */
  backlog: () => import("./cycle/backlog/Backlog.tsx"),
  /** The Team's archive (2026-11-03, 001 FR-186): what an import holds until it is picked into a sprint. */
  archive: () => import("./archive/Archive.tsx"),
  modules: () => import("./module/list/ModulesList.tsx"),
  projectMeetings: () => import("./meetings/project/ProjectMeetings.tsx"),
  projectInsights: () => import("./meetings/project/ProjectInsights.tsx"),
  module: () => import("./module/detail/ModuleDetail.tsx"),
  projectSettings: () => import("./settings/project/layout/Layout.tsx"),
  workspaceSettings: () => import("./settings/workspace/layout/Layout.tsx"),
  profileSettings: () => import("./settings/profile/layout/Layout.tsx"),
  instance: () => import("./settings/instance/layout/Layout.tsx"),
  /** The palette: prefetched on idle by the shell and mounted on its first opening, so `Cmd+K` costs no wait. */
  palette: () => import("./palette/CommandPalette.tsx"),
  views: () => import("./views/manage/ViewsManagement.tsx"),
  view: () => import("./views/open/ViewRoute.tsx"),
  search: () => import("./search/Search.tsx"),
  activity: () => import("./activity/WorkspaceActivity.tsx"),
  desktopAuthorize: () => import("./auth/desktop/DesktopAuthorize.tsx"),
} as const;

const ProjectItems = lazy(() => chunks.collection().then((module) => ({ default: module.ProjectItemsRoute })));
const AllItems = lazy(() => chunks.allItems().then((module) => ({ default: module.AllItems })));
const Home = lazy(() => chunks.dashboard().then((module) => ({ default: module.HomeRoute })));
const Overview = lazy(() => chunks.overview().then((module) => ({ default: module.OverviewRoute })));
const MyWork = lazy(() => chunks.myWork().then((module) => ({ default: module.MyWork })));
const ItemPage = lazy(() => chunks.item().then((module) => ({ default: module.ItemPage })));
const Inbox = lazy(() => chunks.inbox().then((module) => ({ default: module.Inbox })));
const ProjectsIndex = lazy(() => chunks.projects().then((module) => ({ default: module.ProjectsIndex })));
const Accounts = lazy(() => chunks.accounts().then((module) => ({ default: module.AccountsRoute })));
const AccountPage = lazy(() => chunks.accountPage().then((module) => ({ default: module.AccountPageRoute })));
const AccountTasks = lazy(() => chunks.accountTasks().then((module) => ({ default: module.AccountTasksRoute })));
const AccountMeetings = lazy(() => chunks.accountMeetings().then((module) => ({ default: module.AccountMeetingsRoute })));
const AccountActivity = lazy(() => chunks.accountActivity().then((module) => ({ default: module.AccountActivityRoute })));
const AccountInsights = lazy(() => chunks.accountInsights().then((module) => ({ default: module.ClientInsightsRoute })));
const AccountGroups = lazy(() => chunks.accountGroups().then((module) => ({ default: module.AccountGroupsRoute })));
const UnassignedEmails = lazy(() => chunks.unassignedEmails().then((module) => ({ default: module.UnassignedEmailsRoute })));
const EmailPage = lazy(() => chunks.emailPage().then((module) => ({ default: module.EmailPageRoute })));
const ClientItems = lazy(() => chunks.clientItems().then((module) => ({ default: module.ClientItemsRoute })));
const TicketsHome = lazy(() => chunks.tickets().then((module) => ({ default: module.TicketsHomeRoute })));
const PortalPage = lazy(() => chunks.portalPage().then((module) => ({ default: module.PortalPageRoute })));
const TicketPage = lazy(() => chunks.ticketPage().then((module) => ({ default: module.TicketPageRoute })));
const MessagesPage = lazy(() => chunks.messagesPage().then((module) => ({ default: module.MessagesPageRoute })));
const ClientStatusReport = lazy(() => chunks.clientStatusReport().then((module) => ({ default: module.StatusReportRoute })));
const Library = lazy(() => chunks.library().then((module) => ({ default: module.LibraryRoute })));
const Meetings = lazy(() => chunks.meetings().then((module) => ({ default: module.MeetingsRoute })));
const MeetingPage = lazy(() => chunks.meeting().then((module) => ({ default: module.MeetingPageRoute })));
const MeetingsDecide = lazy(() => chunks.meetingsDecide().then((module) => ({ default: module.DecideRoute })));
const Cycles = lazy(() => chunks.cycles().then((module) => ({ default: module.CyclesRoute })));
const CycleDetail = lazy(() => chunks.cycle().then((module) => ({ default: module.CycleDetailRoute })));
const Backlog = lazy(() => chunks.backlog().then((module) => ({ default: module.BacklogRoute })));
const Archive = lazy(() => chunks.archive().then((module) => ({ default: module.ArchiveRoute })));
const Modules = lazy(() => chunks.modules().then((module) => ({ default: module.ModulesRoute })));
const ProjectMeetings = lazy(() => chunks.projectMeetings().then((module) => ({ default: module.ProjectMeetingsRoute })));
const ProjectInsights = lazy(() => chunks.projectInsights().then((module) => ({ default: module.ProjectInsightsRoute })));
const ModuleDetail = lazy(() => chunks.module().then((module) => ({ default: module.ModuleDetailRoute })));
const ProjectSettings = lazy(() => chunks.projectSettings().then((module) => ({ default: module.ProjectSettingsRoute })));
const WorkspaceSettings = lazy(() => chunks.workspaceSettings().then((module) => ({ default: module.WorkspaceSettingsRoute })));
const ProfileSettings = lazy(() => chunks.profileSettings().then((module) => ({ default: module.ProfileSettingsRoute })));
const Instance = lazy(() => chunks.instance().then((module) => ({ default: module.InstanceRoute })));
const Onboarding = lazy(() => chunks.onboarding().then((module) => ({ default: module.Onboarding })));
const Welcome = lazy(() => chunks.welcome().then((module) => ({ default: module.Welcome })));
const NewWorkspace = lazy(() => chunks.newWorkspace().then((module) => ({ default: module.NewWorkspace })));
const ViewsManagement = lazy(() => chunks.views().then((module) => ({ default: module.ViewsManagementRoute })));
const SavedView = lazy(() => chunks.view().then((module) => ({ default: module.SavedViewRoute })));
const SearchPage = lazy(() => chunks.search().then((module) => ({ default: module.SearchRoute })));
const WorkspaceActivity = lazy(() => chunks.activity().then((module) => ({ default: module.WorkspaceActivityRoute })));
const DesktopAuthorize = lazy(() => chunks.desktopAuthorize().then((module) => ({ default: module.DesktopAuthorizeRoute })));

/**
 * The component gallery (`contracts/design-system.md` § Component gallery, 2026-10-15): `/_gallery`,
 * registered when this is a development build or the build was made with `BATON_GALLERY=1`, the
 * gallery's CI build. The flag is a build-time constant, so a production build carries neither the
 * route nor the gallery's chunk, which `scripts/size.ts` holds it to. Not a product route: it is
 * outside `paths`, `contracts/screens.md` and the route walk of SC-015-011.
 */
const GALLERY_ENABLED = import.meta.env.DEV || import.meta.env.BATON_GALLERY === "1";
const Gallery = GALLERY_ENABLED ? lazy(() => import("./gallery/Gallery.tsx").then((module) => ({ default: module.Gallery }))) : null;

/**
 * A lazy screen behind its own skeleton, so the chunk's wait looks like the screen's first load
 * (FR-015-090). The route is one skeleton scope (FR-015-194): the fallback takes the hold, and the
 * screen's own region, mounting as the fallback leaves, continues it rather than holding again.
 */
function deferred(element: ReactNode, fallback: ReactNode): ReactNode {
  return (
    <SkeletonHoldScope>
      <NavigationFocusMark />
      <Suspense fallback={fallback}>{element}</Suspense>
    </SkeletonHoldScope>
  );
}

const routes: RouteObject[] = [
  { path: paths.root, element: <RootRedirect /> },
  ...(Gallery === null ? [] : [{ path: "/_gallery", element: deferred(<Gallery />, null) }]),
  { path: paths.onboarding, element: deferred(<Onboarding />, <WizardSkeleton />) },
  { path: paths.welcome, element: deferred(<Welcome />, <WizardSkeleton />) },
  { path: paths.newWorkspace, element: deferred(<NewWorkspace />, <WizardSkeleton />) },
  {
    path: paths.desktopAuthorize,
    element: deferred(<DesktopAuthorize />, <WizardSkeleton />),
    errorElement: (
      <OutsideShell>
        <RouteError />
      </OutsideShell>
    ),
  },
  ...[paths.profileSettings, paths.instance].map<RouteObject>((path) => ({
    path,
    element: deferred(path === paths.profileSettings ? <ProfileSettings /> : <Instance />, <SettingsFrameSkeleton pages={path === paths.profileSettings ? 5 : 3} />),
    errorElement: (
      <OutsideShell>
        <RouteError />
      </OutsideShell>
    ),
  })),
  {
    path: paths.workspace,
    element: <WorkspaceEntry />,
    errorElement: (
      <OutsideShell>
        <RouteError />
      </OutsideShell>
    ),
    children: [
      { index: true, element: <WorkspaceIndexRedirect /> },
      { path: "dashboard", element: deferred(<Home />, <HomeSkeleton />), errorElement: <RouteError /> },
      { path: "overview", element: deferred(<Overview />, <OverviewSkeleton />), errorElement: <RouteError /> },
      { path: "inbox", element: deferred(<Inbox />, <InboxSkeleton />), errorElement: <RouteError /> },
      { path: "my-work", element: deferred(<MyWork />, <MyWorkSkeleton />), errorElement: <RouteError /> },
      { path: "all-tasks", element: deferred(<AllItems />, <AllItemsSkeleton />), errorElement: <RouteError /> },
      { path: "backlog", element: deferred(<Backlog />, <AllItemsSkeleton />), errorElement: <RouteError /> },
      { path: "archive", element: deferred(<Archive />, <AllItemsSkeleton />), errorElement: <RouteError /> },
      // The old spellings of the routes below, kept as redirects (2026-09-18): cycles, items and all-items.
      { path: "all-items", element: <LegacyRedirect /> },
      { path: "cycles/*", element: <LegacyRedirect /> },
      { path: "cycles", element: <LegacyRedirect /> },
      { path: "items/*", element: <LegacyRedirect /> },
      { path: "projects/:project/items", element: <LegacyRedirect /> },
      // The client routes' old spelling, kept as redirects (2026-09-29): accounts and account-groups.
      { path: "accounts/*", element: <LegacyRedirect /> },
      { path: "accounts", element: <LegacyRedirect /> },
      { path: "account-groups", element: <LegacyRedirect /> },
      // The backlog's first address, under the sprints (2026-10-02, moved to `/{ws}/backlog` the same day by the owner's decision).
      { path: "sprints/backlog", element: <LegacyRedirect /> },
      { path: "search", element: deferred(<SearchPage />, <AllItemsSkeleton />), errorElement: <RouteError /> },
      { path: "activity", element: deferred(<WorkspaceActivity />, <InboxSkeleton />), errorElement: <RouteError /> },
      { path: "projects", element: deferred(<ProjectsIndex />, <ProjectsIndexSkeleton />), errorElement: <RouteError /> },
      { path: "projects/:project", element: <ProjectRedirect />, errorElement: <RouteError /> },
      { path: "projects/:project/tasks", element: deferred(<ProjectItems />, <ProjectItemsFallback />), errorElement: <RouteError /> },
      { path: "projects/:project/views", element: deferred(<ViewsManagement />, <ViewsManagementSkeleton />), errorElement: <RouteError /> },
      { path: "projects/:project/views/:view", element: deferred(<SavedView />, <ProjectItemsFallback />), errorElement: <RouteError /> },
      { path: "views", element: deferred(<ViewsManagement />, <ViewsManagementSkeleton />), errorElement: <RouteError /> },
      { path: "views/:view", element: deferred(<SavedView />, <AllItemsSkeleton />), errorElement: <RouteError /> },
      { path: "clients", element: deferred(<Accounts />, <AccountsFallback />), errorElement: <RouteError /> },
      // A contact's side peek is a child of the client's page, which the shell's peek slot draws over it (`shell/peek/PeekSlot.tsx`), so opening and closing it keeps the page mounted.
      { path: "clients/:account", element: deferred(<AccountPage />, <AccountHubSkeleton />), errorElement: <RouteError />, children: [{ path: "contacts/:contact", element: null }] },
      { path: "clients/:account/tasks", element: deferred(<AccountTasks />, <AllItemsSkeleton />), errorElement: <RouteError /> },
      { path: "clients/:account/meetings", element: deferred(<AccountMeetings />, <AccountsPageSkeleton columns={6} controls={0} />), errorElement: <RouteError /> },
      { path: "clients/:account/activity", element: deferred(<AccountActivity />, <ActivityPageSkeleton />), errorElement: <RouteError /> },
      { path: "clients/:account/insights", element: deferred(<AccountInsights />, <AccountsPageSkeleton columns={2} controls={0} />), errorElement: <RouteError /> },
      // The client's insights page by its path before 2026-10-02.
      { path: "clients/:account/findings", element: <ClientInsightsRedirect /> },
      { path: "clients/:account/meetings/:meeting", element: deferred(<MeetingPage />, <MeetingPageSkeleton />), errorElement: <RouteError /> },
      { path: "clients/:account/tasks/:item", element: deferred(<ItemPage />, <ItemDetailSkeleton presentation="page" />), errorElement: <RouteError /> },
      { path: "clients/:account/emails/:email", element: deferred(<EmailPage />, <EmailPageSkeleton />), errorElement: <RouteError /> },
      { path: "tickets", element: deferred(<TicketsHome />, <AccountsPageSkeleton columns={3} controls={0} />), errorElement: <RouteError /> },
      { path: "tickets/:account", element: deferred(<PortalPage />, <AccountsPageSkeleton columns={2} controls={0} />), errorElement: <RouteError /> },
      { path: "tickets/:account/threads", element: deferred(<MessagesPage />, <AccountsPageSkeleton columns={2} controls={0} />), errorElement: <RouteError /> },
      { path: "tickets/:account/threads/:thread", element: deferred(<MessagesPage />, <AccountsPageSkeleton columns={2} controls={0} />), errorElement: <RouteError /> },
      { path: "tickets/:account/:request", element: deferred(<TicketPage />, <AccountsPageSkeleton columns={2} controls={0} />), errorElement: <RouteError /> },
      { path: "client-groups", element: deferred(<AccountGroups />, <AccountsPageSkeleton columns={5} controls={1} />), errorElement: <RouteError /> },
      { path: "client-tasks", element: deferred(<ClientItems />, <AllItemsSkeleton />), errorElement: <RouteError /> },
      // Client tasks' address while the page was named Client items, kept as a redirect (2026-10-13).
      { path: "client-items", element: <LegacyRedirect /> },
      { path: "clients/status-report", element: deferred(<ClientStatusReport />, <AccountsPageSkeleton columns={9} controls={2} />), errorElement: <RouteError /> },
      { path: "unassigned-emails", element: deferred(<UnassignedEmails />, <UnassignedEmailsSkeleton />), errorElement: <RouteError /> },
      { path: "unassigned-emails/:email", element: deferred(<EmailPage />, <EmailPageSkeleton />), errorElement: <RouteError /> },
      { path: "library", element: deferred(<Library />, <LibrarySkeleton />), errorElement: <RouteError /> },
      { path: "meetings", element: deferred(<Meetings />, <MeetingsFallback />), errorElement: <RouteError /> },
      { path: "meetings/to-decide", element: deferred(<MeetingsDecide />, <MeetingsFallback />), errorElement: <RouteError /> },
      { path: "meetings/:meeting", element: deferred(<MeetingPage />, <MeetingPageSkeleton />), errorElement: <RouteError /> },
      { path: "sprints", element: deferred(<Cycles />, <CyclesListSkeleton />), errorElement: <RouteError /> },
      { path: "sprints/active", element: <CycleActiveRedirect />, errorElement: <RouteError /> },
      { path: "sprints/:cycle", element: deferred(<CycleDetail />, <CycleDetailSkeleton />), errorElement: <RouteError /> },
      { path: "projects/:project/modules", element: deferred(<Modules />, <ProjectsIndexSkeleton />), errorElement: <RouteError /> },
      { path: "projects/:project/meetings", element: deferred(<ProjectMeetings />, <ProjectsIndexSkeleton />), errorElement: <RouteError /> },
      { path: "projects/:project/insights", element: deferred(<ProjectInsights />, <ProjectsIndexSkeleton />), errorElement: <RouteError /> },
      { path: "projects/:project/modules/:module", element: deferred(<ModuleDetail />, <ModuleDetailSkeleton />), errorElement: <RouteError /> },
      { path: "projects/:project/settings", element: <ProjectSettingsRedirect />, errorElement: <RouteError /> },
      { path: "projects/:project/settings/:page", element: deferred(<ProjectSettings />, <SettingsRailSkeleton pages={6} />), errorElement: <RouteError /> },
      { path: "settings", element: <WorkspaceSettingsRedirect />, errorElement: <RouteError /> },
      ...["settings/:page", "settings/:page/:detail", "settings/:page/:sub/:detail"].map<RouteObject>((path) => ({
        path,
        element: deferred(<WorkspaceSettings />, (
          <div className="b-page">
            <SettingsPageSkeleton />
          </div>
        )),
        errorElement: <RouteError />,
      })),
      { path: "tasks/:item", element: deferred(<ItemPage />, <ItemDetailSkeleton presentation="page" />), errorElement: <RouteError /> },
      { path: "*", element: <NotFound /> },
    ],
  },
  {
    path: "*",
    element: (
      <OutsideShell>
        <NotFound />
      </OutsideShell>
    ),
  },
];

/** The router, under a basename inside the Microsoft Teams tab (`teams/tab.ts`, 2026-10-02) and at the origin's root otherwise, recording where each page was opened from for the top bar's Back (`shell/history/origin.ts`, 2026-10-07). */
export function createRouter(basename?: string) {
  const router = createBrowserRouter(routes, basename === undefined ? {} : { basename });
  trackOrigins(router);
  return router;
}
